import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { CircleSlash } from "lucide-react";
import { ActionLink } from "@/components/action-link";
import { CheckoutShell } from "@/components/checkout-shell";
import { Ledger, LedgerRow } from "@/components/ledger";
import { StatusBlock } from "@/components/status-block";
import { buildCanceledReturnUrl, hostOf } from "@/lib/return-url";
import {
  SESSION_REQUEST_INIT,
  parseSessionStatus,
  sessionEndpoint,
} from "@/lib/session-status";

/**
 * Where Stripe sends a shopper who backed out.
 *
 * Nothing is pending on a cancel, so unlike the return page this does exactly
 * one request, and only to recover WHERE to send them — the `return_url` the
 * consumer app registered, which the browser was never told. Any failure just
 * leaves the generic sara.ai link; there is nothing here worth retrying and
 * nothing worth showing an error about.
 */
export function CancelPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const backUrl = useCanceledReturnUrl(sessionId);
  const backHost = hostOf(backUrl ?? undefined);

  return (
    <CheckoutShell title="Payment canceled · Sara's Books">
      <StatusBlock
        tone="held"
        icon={<CircleSlash className="size-4.5" aria-hidden />}
        title="Payment canceled"
        body="No payment was taken and you have not been charged. You can return and try again whenever you’re ready."
      >
        {/* One row, and it answers the only question anyone actually has on
            this page. Verdigris — the settled tone — because "none" is the
            good outcome here, not a lack of one. */}
        <Ledger>
          <LedgerRow label="Charge" value="none" tone="settled" delayMs={90} />
        </Ledger>

        {backUrl !== null && backHost !== null ? (
          <ActionLink href={backUrl}>Return to {backHost}</ActionLink>
        ) : (
          <ActionLink href="https://sara.ai" variant="outline">
            Return to sara.ai
          </ActionLink>
        )}
      </StatusBlock>
    </CheckoutShell>
  );
}

/**
 * One-shot lookup of the consumer's return URL, flagged canceled.
 *
 * An effect because it is a network read that runs because the page was
 * displayed — the sanctioned case. The `cancelled` flag is the race guard
 * required by react-useeffect-discipline §3.2; without it, `StrictMode`'s
 * double mount would resolve a response into an unmounted tree.
 */
function useCanceledReturnUrl(sessionId: string | null): string | null {
  const [backUrl, setBackUrl] = useState<string | null>(null);

  useEffect(() => {
    // Falsy, matching the source's `if (!sessionId)`: a bare `?session_id=`
    // reads back as "" and would otherwise fire a request at a path with no
    // id on it.
    if (!sessionId) return;
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(
          sessionEndpoint(sessionId),
          SESSION_REQUEST_INIT,
        );
        if (!res.ok || cancelled) return;
        const status = parseSessionStatus(await res.json());
        if (cancelled || status?.return_url === undefined) return;
        setBackUrl(buildCanceledReturnUrl(status.return_url));
      } catch {
        // Ignore — the generic return link is already on screen.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return backUrl;
}
